import { CONSENT_NAMES } from "../types/consents";
import { axiosClient } from "./axiosClient";

export const register = (
  email: string,
  password: string,
  passwordConfirmation: string,
  isOrganization: boolean,
  isApplicationServiceTermsAccepted: boolean,
  isApplicationMarketingTermsAccepted: boolean
) =>
  axiosClient.post("user", {
    user: {
      email,
      password,
      password_confirmation: passwordConfirmation,
      is_organization: isOrganization,
      [CONSENT_NAMES.APPLICATIONS_MARKETING]: isApplicationMarketingTermsAccepted,
      [CONSENT_NAMES.APPLICATIONS_SERVICE]: isApplicationServiceTermsAccepted
    }
  });

export const logIn = (email: string, password: string, trustedDeviceToken?: string | null, webauthnSupported?: boolean) =>
  axiosClient.post("login", {
    user: {
      email,
      password,
      trusted_device_token: trustedDeviceToken,
      webauthn_supported: webauthnSupported
    }
  });

// ── Logowanie przez CyberID (OIDC, zewnętrzny IdP) ──
// #authorize: backend robi PKCE i zwraca gotowy authorize_url. #callback: wymiana code → nasza sesja (kształt jak /login).
export const cyberidAuthorize = (redirectUri: string) =>
  axiosClient.get<{ authorize_url: string; state: string; code_verifier: string; nonce: string }>("oidc/cyberid/authorize", {
    params: { redirect_uri: redirectUri }
  });

export const cyberidCallback = (code: string, codeVerifier: string, redirectUri: string, nonce: string) =>
  axiosClient.post<{ access_token: string; expires_in: number; refresh_expires_in: number }>("oidc/cyberid/callback", {
    code,
    code_verifier: codeVerifier,
    redirect_uri: redirectUri,
    nonce
  });

// Weryfikacja tożsamości WYBORCY (per głosowanie) — zwraca krótkożyciową asercję do dołączenia przy oddaniu głosu.
export const cyberidVerify = (code: string, codeVerifier: string, redirectUri: string, nonce: string, votingId: string) =>
  axiosClient.post<{ assertion: string; email: string; assurance_level: number; expires_in: number }>("oidc/cyberid/verify", {
    code,
    code_verifier: codeVerifier,
    redirect_uri: redirectUri,
    nonce,
    voting_id: votingId
  });

// ── 2FA logowania ──
export const twoFactorApprove = (challengeToken: string) =>
  axiosClient.post("two_factor/approve", { challenge_token: challengeToken });

// Passkey jako 2FA: opcje WebAuthn dla wyzwania + weryfikacja asercji → sesja.
export const twoFactorPasskeyOptions = (challengeToken: string) =>
  axiosClient.post<{ options: PasskeyOptions["options"]; webauthn_token: string }>("two_factor/passkey/options", {
    challenge_token: challengeToken
  });
export const twoFactorPasskeyVerify = (challengeToken: string, webauthnToken: string, credential: unknown, rememberDevice: boolean) =>
  axiosClient.post("two_factor/passkey/verify", {
    challenge_token: challengeToken,
    webauthn_token: webauthnToken,
    credential,
    remember_device: rememberDevice
  });

export const twoFactorSession = (challengeToken: string, rememberDevice: boolean) =>
  axiosClient.post("two_factor/session", { challenge_token: challengeToken, remember_device: rememberDevice });

export const twoFactorVerify = (challengeToken: string, code: string, rememberDevice: boolean) =>
  axiosClient.post("two_factor/verify", { challenge_token: challengeToken, code, remember_device: rememberDevice });

export const twoFactorSendEmail = (challengeToken: string) =>
  axiosClient.post("two_factor/email", { challenge_token: challengeToken });

export const setTwoFactor = (enabled: boolean) =>
  axiosClient.put("user", { user: { two_factor_enabled: enabled } });

// Polityka „zawsze pytaj o 2FA" (ignoruj zaufane urządzenie).
export const setTwoFactorAlways = (enabled: boolean) =>
  axiosClient.put("user", { user: { two_factor_always: enabled } });

// ── 3. metoda 2FA: kod z aplikacji authenticator (TOTP) — enrollment (zalogowany) ──
export type TotpSetup = { provisioning_uri: string; secret: string; issuer: string; account: string };

export const twoFactorTotpSetup = () =>
  axiosClient.post<TotpSetup>("two_factor/totp/setup");

export const twoFactorTotpEnable = (code: string) =>
  axiosClient.post("two_factor/totp/enable", { code });

export const twoFactorTotpDisable = () =>
  axiosClient.delete("two_factor/totp");

// ── Centrum Bezpieczeństwa: aktywne sesje ──
export type ActiveSession = {
  id: string;
  device: string;
  ip: string | null;
  last_used_at: string;
  created_at: string;
  current: boolean;
};

export const getActiveSessions = () => axiosClient.get<ActiveSession[]>("active_sessions");
export const revokeSession = (id: string) => axiosClient.delete(`active_sessions/${id}`);
export const revokeOtherSessions = () => axiosClient.delete("active_sessions");

// ── Centrum Bezpieczeństwa: kody zapasowe ──
export type RecoveryStatus = { generated: boolean; remaining: number };

export const getRecoveryStatus = () => axiosClient.get<RecoveryStatus>("recovery_codes");
export const generateRecoveryCodes = () => axiosClient.post<{ codes: string[]; remaining: number }>("recovery_codes");

// ── Centrum Bezpieczeństwa: passkey (WebAuthn) ──
export type Passkey = { id: string; nickname: string; last_used_at: string | null; created_at: string };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PasskeyOptions = { options: any; challenge_token: string };

export const getPasskeys = () => axiosClient.get<Passkey[]>("passkeys");
export const passkeyRegisterOptions = () => axiosClient.post<PasskeyOptions>("passkeys/options");
export const passkeyRegisterVerify = (credential: unknown, challengeToken: string, nickname?: string) =>
  axiosClient.post("passkeys", { credential, challenge_token: challengeToken, nickname });
export const deletePasskey = (id: string) => axiosClient.delete(`passkeys/${id}`);
export const passkeyLoginOptions = (email: string) =>
  axiosClient.post<PasskeyOptions>("passkeys/login_options", { email });
export const passkeyLoginVerify = (credential: unknown, challengeToken: string) =>
  axiosClient.post("passkeys/login", { credential, challenge_token: challengeToken });

export type ProfileSettingsPayload = {
  profile_public?: boolean;
  profile_bio?: string | null;
  profile_show_votings?: boolean;
  profile_show_groups?: boolean;
  profile_show_email?: boolean;
  profile_show_org_details?: boolean;
  profile_show_address?: boolean;
  profile_show_posts?: boolean;
  allow_group_add?: boolean;
};

export const updateProfileSettings = (fields: ProfileSettingsPayload) =>
  axiosClient.put<{ user: import("../types/profile").GetUserDetails }>("user", { user: fields });

export const validateRecaptcha = (token: string | null | undefined) =>
  axiosClient.post("recaptcha", {
    recaptcha: {
      token
    }
  });

export const refreshToken = () => axiosClient.post("refresh_token");

export const logOut = () => axiosClient.delete("login");

export const activateAccount = (token: string) =>
  axiosClient.post("user/activate", {
    user: {
      activation_token: token
    }
  });

export const sendPasswordResetEmail = (email: string) =>
  axiosClient.post("user/password/recovery_email", {
    email
  });

export const resetPassword = (password: string, confirmPassword: string, token: string) =>
  axiosClient.put("user/password", {
    token,
    user: {
      password,
      password_confirmation: confirmPassword
    }
  });

export const deactivateAccountSendEMail = () => axiosClient.post("user/removal_email");
export const confirmDeactivateAccount = async (token: string) =>
  axiosClient.delete("user", { data: { removal_token: token } });
